import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Divider,
  TextField,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import { Project, Slide } from '../../types';
import { Slide as ContentSlicingSlide, ChatMessage, GenerateSlideResponse, ContentDensityPreference } from '../../types/contentSlicing';
import { useLang } from '../../contexts/langContext';
import { getMaterialById } from '../../services/materials';
import api from '../../services/api';
import { chunkText, extractWrappedLines, getTotalWrappedLines, TextChunk } from '../../utils/textFormatting';
import { countTokens } from 'token-estimator';
import CurrentSlideReviewPanel from '../contentSlicing/CurrentSlideReviewPanel';
import CoverFlowView from '../contentSlicing/CoverFlowView';

// Pre-defined user preferences
const PREDEFINED_PREFERENCES = [
  { en: 'Add section title slide before each section content', zh: '在每个章节内容前添加章节标题页' },
  { en: 'Generate a new slide for table of contents after title slide', zh: '在标题页的下一页生成一个新的目录页' },
  { en: 'Add transition slides between major sections', zh: '在主要章节之间添加过渡页' },
  { en: 'Include summary slide at the end', zh: '在末尾添加总结页' },
  { en: 'Add thank you slide at the end', zh: '在末尾添加致谢页' },
];

const CONTENT_DENSITY_OPTIONS: Array<{
  key: ContentDensityPreference;
  lines: number;
  label: { en: string; zh: string };
}> = [
  {
    key: 'very_concise',
    lines: 3,
    label: { en: 'Very concise', zh: '极度精简' },
  },
  {
    key: 'concise',
    lines: 5,
    label: { en: 'Concise', zh: '精简' },
  },
  {
    key: 'normal',
    lines: 8,
    label: { en: 'Normal', zh: '适中' },
  },
  {
    key: 'rich',
    lines: 12,
    label: { en: 'Rich', zh: '丰富' },
  },
  {
    key: 'very_rich',
    lines: 18,
    label: { en: 'Very rich', zh: '非常丰富' },
  },
];

const DEFAULT_CONTENT_DENSITY: ContentDensityPreference = 'normal';

const hasDoneSignal = (history: ChatMessage[]): boolean =>
  history.some((msg) => {
    if (msg.role !== 'assistant') return false;
    try {
      const parsed = JSON.parse(msg.content);
      return Boolean(parsed?.done);
    } catch {
      return false;
    }
  });

const isGeneratedSlide = (slide: ContentSlicingSlide): boolean => slide.rangeStartLine === -1 && slide.rangeEndLine === -1;

const toGlobalSlideRange = (slide: ContentSlicingSlide, offset: number): ContentSlicingSlide => {
  if (isGeneratedSlide(slide)) {
    return slide;
  }

  return {
    ...slide,
    rangeStartLine: slide.rangeStartLine + offset,
    rangeEndLine: slide.rangeEndLine + offset,
  };
};

const toChunkRelativeSlideRange = (slide: ContentSlicingSlide, offset: number): ContentSlicingSlide => {
  if (isGeneratedSlide(slide)) {
    return slide;
  }

  return {
    ...slide,
    rangeStartLine: slide.rangeStartLine - offset,
    rangeEndLine: slide.rangeEndLine - offset,
  };
};

interface SlideSlicerProps {
  project: Project;
  onUpdateSlides: (slides: Slide[]) => Promise<void>;
}

export interface SlideSlicerRef {
  triggerClick: () => void;
}

const SlideSlicer = forwardRef<SlideSlicerRef, SlideSlicerProps>(({ project, onUpdateSlides }, ref) => {
  const { lang } = useLang();
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  // Expose trigger click method to parent
  useImperativeHandle(ref, () => ({
    triggerClick: () => {
      triggerButtonRef.current?.click();
    },
  }));

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [materialText, setMaterialText] = useState('');
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialError, setMaterialError] = useState('');

  // Token validation
  const [tokenValidationDialogOpen, setTokenValidationDialogOpen] = useState(false);
  const [tokenValidationType, setTokenValidationType] = useState<'error' | 'warning'>('warning');
  const [tokenCount, setTokenCount] = useState(0);

  // Text preview
  const [textPreviewExpanded, setTextPreviewExpanded] = useState(false);

  // User preferences
  const [userPreferences, setUserPreferences] = useState('');
  const [contentDensity, setContentDensity] = useState<ContentDensityPreference>(DEFAULT_CONTENT_DENSITY);

  // Chunking state
  const [chunks, setChunks] = useState<TextChunk[]>([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);

  // Slicing workflow state
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [acceptedSlides, setAcceptedSlides] = useState<ContentSlicingSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState<ContentSlicingSlide | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedDensityOption =
    CONTENT_DENSITY_OPTIONS.find((option) => option.key === contentDensity) ||
    CONTENT_DENSITY_OPTIONS.find((option) => option.key === DEFAULT_CONTENT_DENSITY)!;
  const contentLineTarget = selectedDensityOption.lines;

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    setMaterialLoading(true);
    setMaterialError('');

    try {
      const material = await getMaterialById(project.verbatimMaterialId!);
      const text = material.fileMetadata?.parsedText || '';

      if (!text.trim()) {
        setMaterialError(lang === 'en' ? 'Material has no text content.' : '材料没有文本内容。');
        setMaterialLoading(false);
        return;
      }

      setMaterialText(text);

      // Validate token count
      const tokens = countTokens(text);
      setTokenCount(tokens);

      console.log(`Material loaded: ${tokens} tokens, ${getTotalWrappedLines(text)} wrapped lines`);

      if (tokens > 100000) {
        setTokenValidationType('error');
        setTokenValidationDialogOpen(true);
      } else if (tokens > 50000) {
        setTokenValidationType('warning');
        setTokenValidationDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching material:', error);
      setMaterialError(lang === 'en' ? 'Failed to load material.' : '加载材料失败。');
    } finally {
      setMaterialLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (sessionStarted && !allDone) {
      const confirmed = window.confirm(lang === 'en' ? 'Slicing is in progress. Close anyway?' : '剪切正在进行中。仍然关闭？');
      if (!confirmed) return;
    }
    setDialogOpen(false);
    resetState();
  };

  const resetState = () => {
    setMaterialText('');
    setMaterialError('');
    setTextPreviewExpanded(false);
    setUserPreferences('');
    setContentDensity(DEFAULT_CONTENT_DENSITY);
    setChunks([]);
    setCurrentChunkIndex(0);
    setChatHistory([]);
    setAcceptedSlides([]);
    setCurrentSlide(null);
    setSessionStarted(false);
    setAllDone(false);
    setTokenCount(0);
  };

  const handleContentDensityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ContentDensityPreference;
    setContentDensity(value);
  };

  const handleTokenValidationResponse = (proceed: boolean) => {
    setTokenValidationDialogOpen(false);
    if (!proceed || tokenValidationType === 'error') {
      if (tokenValidationType === 'error') {
        setDialogOpen(false);
        resetState();
      }
    }
  };

  const handleStart = async () => {
    setSessionStarted(true);
    setLoading(true);

    try {
      // Chunk the text
      const textChunks = chunkText(materialText, 20000, 10);
      setChunks(textChunks);
      setCurrentChunkIndex(0);

      console.log(`=== Text split into ${textChunks.length} chunks (20000 tokens each) ===`);
      textChunks.forEach((chunk, idx) => {
        console.log(
          `Chunk ${idx + 1}: ${chunk.tokenCount} tokens, ${chunk.totalWrappedLines} wrapped lines (source lines ${chunk.sourceStartLine}-${
            chunk.sourceEndLine
          })`
        );
      });

      // Start with first chunk
      const firstChunk = textChunks[0];
      const response = await api().post<GenerateSlideResponse>('/content-slicing/generate-slide', {
        text: firstChunk.text,
        chatHistory: [],
        instruction: 'Generate first slide',
        isRegenerate: false,
        userPreferences,
        contentDensity,
      });

      const chunkDone = response.data.allTextCovered || hasDoneSignal(response.data.chatHistory);

      // Adjust slide line numbers by chunk offset
      if (response.data.slide) {
        const adjustedSlide = toGlobalSlideRange(response.data.slide, firstChunk.globalWrappedLineOffset);
        setCurrentSlide(adjustedSlide);
      } else if (chunkDone) {
        setAllDone(true);
        setCurrentSlide(null);
      }
      setChatHistory(response.data.chatHistory);
    } catch (error) {
      console.error('Error generating first slide:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestartSession = async () => {
    if (!sessionStarted || !materialText || loading) {
      return;
    }

    const confirmed = window.confirm(lang === 'en' ? 'Restart slicing from the beginning?' : '从头重新开始剪切？');
    if (!confirmed) return;

    setAcceptedSlides([]);
    setChatHistory([]);
    setCurrentSlide(null);
    setChunks([]);
    setCurrentChunkIndex(0);
    setSessionStarted(false);
    setAllDone(false);
    setSaving(false);

    try {
      await handleStart();
    } catch (error) {
      console.error('Error restarting slicing session:', error);
    }
  };

  const handleAccept = async () => {
    if (!currentSlide || chunks.length === 0) return;

    const newAcceptedSlides = [...acceptedSlides, currentSlide];
    setAcceptedSlides(newAcceptedSlides);

    const currentChunk = chunks[currentChunkIndex];

    // Convert currentSlide back to chunk-relative for chat history
    const chunkRelativeSlide = toChunkRelativeSlideRange(currentSlide, currentChunk.globalWrappedLineOffset);

    // Check if current chunk is fully covered
    const chunkTotalLines = currentChunk.totalWrappedLines;
    const chunkCoveredLines = chunkRelativeSlide.rangeEndLine;

    if (chunkCoveredLines >= chunkTotalLines) {
      console.log(`✓ Chunk ${currentChunkIndex + 1} completed`);

      // Move to next chunk
      if (currentChunkIndex < chunks.length - 1) {
        const nextChunkIndex = currentChunkIndex + 1;
        setCurrentChunkIndex(nextChunkIndex);
        setChatHistory([]); // Reset chat history for new chunk
        setLoading(true);

        try {
          const nextChunk = chunks[nextChunkIndex];
          console.log(`Starting Chunk ${nextChunkIndex + 1}/${chunks.length}`);

          const response = await api().post<GenerateSlideResponse>('/content-slicing/generate-slide', {
            text: nextChunk.text,
            chatHistory: [],
            instruction: 'Generate first slide',
            isRegenerate: false,
            userPreferences,
            contentDensity,
          });

          // Adjust slide line numbers by chunk offset
          if (response.data.slide) {
            const adjustedSlide = toGlobalSlideRange(response.data.slide, nextChunk.globalWrappedLineOffset);
            setCurrentSlide(adjustedSlide);
          }
          setChatHistory(response.data.chatHistory);
        } catch (error) {
          console.error('Error starting next chunk:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // All chunks completed
        setAllDone(true);
        setCurrentSlide(null);
        console.log('=== All chunks completed ===');
      }
      return;
    }

    // Generate next slide within current chunk
    setLoading(true);
    try {
      const response = await api().post<GenerateSlideResponse>('/content-slicing/generate-slide', {
        text: currentChunk.text,
        chatHistory: [...chatHistory, { role: 'user', content: 'accept' }, { role: 'assistant', content: JSON.stringify(chunkRelativeSlide) }],
        instruction: 'Generate next slide',
        isRegenerate: false,
        userPreferences,
        contentDensity,
      });

      const chunkDone = response.data.allTextCovered || hasDoneSignal(response.data.chatHistory);

      // Adjust slide line numbers by chunk offset
      if (response.data.slide) {
        const adjustedSlide = toGlobalSlideRange(response.data.slide, currentChunk.globalWrappedLineOffset);
        setCurrentSlide(adjustedSlide);
      } else if (chunkDone) {
        setCurrentSlide(null);
      }
      setChatHistory(response.data.chatHistory);

      // Check if backend says chunk is covered
      if (chunkDone) {
        console.log(`✓ Chunk ${currentChunkIndex + 1} completed (backend reported)`);

        if (currentChunkIndex < chunks.length - 1) {
          // Move to next chunk
          const nextChunkIndex = currentChunkIndex + 1;
          setCurrentChunkIndex(nextChunkIndex);
          setChatHistory([]); // Reset chat history

          // Add current slide if exists
          let slidesForNextChunk = newAcceptedSlides;
          if (response.data.slide) {
            const adjustedSlide = toGlobalSlideRange(response.data.slide, currentChunk.globalWrappedLineOffset);
            slidesForNextChunk = [...newAcceptedSlides, adjustedSlide];
            setAcceptedSlides(slidesForNextChunk);
          }

          // Start next chunk
          setLoading(true);
          try {
            const nextChunk = chunks[nextChunkIndex];
            console.log(`Starting Chunk ${nextChunkIndex + 1}/${chunks.length}`);

            const nextResponse = await api().post<GenerateSlideResponse>('/content-slicing/generate-slide', {
              text: nextChunk.text,
              chatHistory: [],
              instruction: 'Generate first slide',
              isRegenerate: false,
              userPreferences,
              contentDensity,
            });

            if (nextResponse.data.slide) {
              const adjustedSlide = toGlobalSlideRange(nextResponse.data.slide, nextChunk.globalWrappedLineOffset);
              setCurrentSlide(adjustedSlide);
            }
            setChatHistory(nextResponse.data.chatHistory);
          } catch (error) {
            console.error('Error starting next chunk:', error);
          } finally {
            setLoading(false);
          }
        } else {
          // All chunks done
          setAllDone(true);
          if (response.data.slide) {
            const finalSlides = [...newAcceptedSlides, response.data.slide];
            setAcceptedSlides(finalSlides);
          }
          setCurrentSlide(null);
          console.log('=== All chunks completed ===');
        }
      }
    } catch (error) {
      console.error('Error generating next slide:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (instruction: string) => {
    if (chunks.length === 0) return;

    setLoading(true);
    try {
      const currentChunk = chunks[currentChunkIndex];
      const response = await api().post<GenerateSlideResponse>('/content-slicing/generate-slide', {
        text: currentChunk.text,
        chatHistory,
        instruction,
        isRegenerate: true,
        userPreferences,
        contentDensity,
      });

      const chunkDone = response.data.allTextCovered || hasDoneSignal(response.data.chatHistory);

      // Adjust slide line numbers by chunk offset
      if (response.data.slide) {
        const adjustedSlide = toGlobalSlideRange(response.data.slide, currentChunk.globalWrappedLineOffset);
        setCurrentSlide(adjustedSlide);
      } else if (chunkDone) {
        setCurrentSlide(null);
        setAllDone(true);
      }
      setChatHistory(response.data.chatHistory);
    } catch (error) {
      console.error('Error regenerating slide:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToProject = async () => {
    setSaving(true);
    try {
      // Map contentSlicing slides to project slides
      const projectSlides: Slide[] = acceptedSlides.map((csSlide) => ({
        rawContent: csSlide.generatedContent || extractWrappedLines(materialText, csSlide.rangeStartLine, csSlide.rangeEndLine),
        contentDescription: csSlide.layoutDescription,
        formData: {},
      }));

      console.log(`=== Saving ${projectSlides.length} slides to project ===`);
      await onUpdateSlides(projectSlides);

      console.log('✓ Slides saved successfully');
      setDialogOpen(false);
      resetState();
    } catch (error) {
      console.error('Error saving slides to project:', error);
    } finally {
      setSaving(false);
    }
  };

  // Calculate covered lines
  const getCoveredLines = (): number => {
    if (acceptedSlides.length === 0) return 0;
    const maxLine = Math.max(...acceptedSlides.map((s) => s.rangeEndLine));
    return maxLine;
  };

  const totalLines = materialText ? getTotalWrappedLines(materialText) : 0;
  const coveredLines = getCoveredLines();
  const estimatedSlideCount = Math.ceil(tokenCount / 400); // Rough estimate: 400 tokens per slide

  return (
    <>
      <Box>
        <Button ref={triggerButtonRef} variant="contained" color="primary" startIcon={<ContentCutIcon />} onClick={handleOpenDialog}>
          {lang === 'en' ? 'Slice Verbatim Material into Slides' : '把材料剪切成幻灯片'}
        </Button>
      </Box>

      {/* Main Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'grey.50',
            height: '95vh',
            maxHeight: '95vh',
            width: '100%',
            m: 0,
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5">{lang === 'en' ? 'Slice Material' : '剪切材料'}</Typography>
              {sessionStarted && !allDone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {lang === 'en' ? `Progress: ${coveredLines} / ${totalLines} lines` : `进度：${coveredLines} / ${totalLines} 行`}
                  </Typography>
                  <Button size="small" variant="text" color="secondary" onClick={handleRestartSession} disabled={loading}>
                    {lang === 'en' ? 'Start over' : '重新开始'}
                  </Button>
                </Box>
              )}
            </Box>
            <Box>
              <IconButton onClick={handleCloseDialog} disabled={saving} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2, pb: 0, height: 'calc(90vh - 64px)' }}>
          {materialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : materialError ? (
            <Alert severity="error">{materialError}</Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, height: '100%' }}>
              {/* Main Content Area */}
              <Box sx={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
                {/* Material Text Preview */}
                {!sessionStarted && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">{lang === 'en' ? 'Material Text' : '材料文本'}</Typography>
                      <IconButton size="small" onClick={() => setTextPreviewExpanded(!textPreviewExpanded)}>
                        {textPreviewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {lang === 'en'
                        ? `${tokenCount.toLocaleString()} tokens, ${totalLines} lines`
                        : `${tokenCount.toLocaleString()} 令牌，${totalLines} 行`}
                    </Typography>
                    <Collapse in={textPreviewExpanded}>
                      <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflowY: 'auto', bgcolor: 'background.paper' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {materialText}
                        </Typography>
                      </Paper>
                    </Collapse>
                  </Paper>
                )}

                {/* User Preferences */}
                {!sessionStarted && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label={lang === 'en' ? 'User Preferences (optional)' : '用户偏好（可选）'}
                      placeholder={lang === 'en' ? 'e.g., "Add section header slides before content"' : '例如："在内容前添加章节标题幻灯片"'}
                      value={userPreferences}
                      onChange={(e) => setUserPreferences(e.target.value)}
                      variant="outlined"
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                        {lang === 'en' ? 'Common preferences (click to add):' : '常用偏好（点击添加）：'}
                      </Typography>
                      {PREDEFINED_PREFERENCES.map((pref, index) => {
                        const prefText = lang === 'en' ? pref.en : pref.zh;
                        const isSelected = userPreferences
                          .split(';')
                          .map((p) => p.trim())
                          .includes(prefText);

                        return (
                          <Chip
                            key={index}
                            label={prefText}
                            onClick={() => {
                              if (isSelected) {
                                // Remove this preference
                                const preferences = userPreferences
                                  .split(';')
                                  .map((p) => p.trim())
                                  .filter((p) => p !== prefText);
                                setUserPreferences(preferences.filter((p) => p).join('; '));
                              } else {
                                // Add this preference
                                const newPrefs = userPreferences ? `${userPreferences}; ${prefText}` : prefText;
                                setUserPreferences(newPrefs);
                              }
                            }}
                            variant={isSelected ? 'filled' : 'outlined'}
                            color={isSelected ? 'primary' : 'default'}
                            size="small"
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Content Density */}
                {!sessionStarted && (
                  <Box sx={{ mb: 3 }}>
                    <FormControl>
                      <FormLabel sx={{ mb: 1 }}>{lang === 'en' ? 'Content detail level of each slide' : '每张内容幻灯片的详略程度'}</FormLabel>
                      <RadioGroup row value={contentDensity} onChange={handleContentDensityChange}>
                        {CONTENT_DENSITY_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.key}
                            value={option.key}
                            control={<Radio size="small" />}
                            label={lang === 'en' ? option.label.en : option.label.zh}
                            sx={{ mr: 2 }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {lang === 'en'
                        ? `Only effective when processed text lacks clear structure. Title, section, and thank you slides stay flexible.`
                        : `仅在处理到的文本缺乏清晰结构时生效。标题、章节过渡页和致谢页不受限制。`}
                    </Typography>
                  </Box>
                )}

                {/* Start Button */}
                {!sessionStarted && (
                  <Box sx={{ mb: 3 }}>
                    <Button variant="contained" color="primary" onClick={handleStart} disabled={loading || !materialText} fullWidth size="large">
                      {lang === 'en' ? 'Start Slicing' : '开始剪切'}
                    </Button>
                  </Box>
                )}

                {/* Current Slide Review */}
                {sessionStarted && !allDone && (
                  <Box sx={{ mb: 3 }}>
                    <CurrentSlideReviewPanel
                      slide={currentSlide}
                      text={materialText}
                      loading={loading}
                      onAccept={handleAccept}
                      onRegenerate={handleRegenerate}
                    />
                  </Box>
                )}

                {/* Completion Message */}
                {allDone && (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {lang === 'en'
                        ? `✓ All text covered! ${acceptedSlides.length} slides created.`
                        : `✓ 所有文本已覆盖！已创建 ${acceptedSlides.length} 张幻灯片。`}
                    </Alert>
                    <Button variant="contained" color="primary" onClick={handleSaveToProject} disabled={saving} fullWidth size="large">
                      {saving ? <CircularProgress size={24} /> : lang === 'en' ? 'Save to Project' : '保存到项目'}
                    </Button>
                  </Box>
                )}

                {/* Accepted Slides Preview - Show below on small/medium screens */}
                {acceptedSlides.length > 0 && (
                  <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                    <Typography variant="h6" gutterBottom>
                      {lang === 'en' ? 'Accepted Slides' : '已接受的幻灯片'}
                    </Typography>
                    <CoverFlowView slides={acceptedSlides} text={materialText} />
                  </Box>
                )}
              </Box>

              {/* Sidebar for Accepted Slides - Only on lg+ screens */}
              {acceptedSlides.length > 0 && (
                <Box sx={{ display: { xs: 'none', lg: 'block' }, width: 400, flexShrink: 0 }}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Typography variant="h6" gutterBottom>
                      {lang === 'en' ? 'Accepted Slides' : '已接受的幻灯片'}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                      <CoverFlowView slides={acceptedSlides} text={materialText} vertical />
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Token Validation Dialog */}
      <Dialog open={tokenValidationDialogOpen} onClose={() => handleTokenValidationResponse(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tokenValidationType === 'error'
            ? lang === 'en'
              ? 'Material Too Large'
              : '材料过大'
            : lang === 'en'
            ? 'Large Material Warning'
            : '大材料警告'}
        </DialogTitle>
        <DialogContent>
          {tokenValidationType === 'error' ? (
            <Typography>
              {lang === 'en'
                ? `This material contains ${tokenCount.toLocaleString()} tokens and cannot be sliced. Please use a smaller document (max 100,000 tokens).`
                : `此材料包含 ${tokenCount.toLocaleString()} 个令牌，无法剪切。请使用较小的文档（最多 100,000 个令牌）。`}
            </Typography>
          ) : (
            <Typography>
              {lang === 'en'
                ? `This material contains ${tokenCount.toLocaleString()} tokens (~${estimatedSlideCount} slides). This may exceed software limits and take significant time. Continue?`
                : `此材料包含 ${tokenCount.toLocaleString()} 个令牌（约 ${estimatedSlideCount} 张幻灯片）。这可能超出软件限制并需要很长时间。继续？`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleTokenValidationResponse(false)}>
            {tokenValidationType === 'error' ? (lang === 'en' ? 'OK' : '确定') : lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          {tokenValidationType === 'warning' && (
            <Button onClick={() => handleTokenValidationResponse(true)} color="warning" variant="contained">
              {lang === 'en' ? 'Continue Anyway' : '仍然继续'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
});

SlideSlicer.displayName = 'SlideSlicer';

export default SlideSlicer;
